import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ListPlus, Wrench, ChevronRight } from "@/components/icons";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { Plaatkop } from "@/components/Plaatkop";
import { PuntQuickDialog } from "@/components/PuntQuickDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import PublicReport from "@/pages/PublicReport";

// A sticker hangs 30cm from your face. Anonymous visitors get the report form;
// TD/owner get the machine itself plus the four things they actually do next.
const StaffScan = ({ qrId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [puntOpen, setPuntOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/qr/resolve/${qrId}`);
        setData(res.data);
      } catch (e) {
        setError(getErrorMessage(e, "Onbekende QR-code."));
      }
    })();
  }, [qrId]);

  if (error)
    return (
      <div className="min-h-screen bg-background p-4">
        <Card className="p-6 text-center text-body text-destructive" data-testid="scan-error">{error}</Card>
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen bg-background p-4">
        <Skeleton className="h-40 w-full" />
      </div>
    );

  const a = data.kind === "asset" ? data.asset : null;
  const loc = data.kind === "location" ? data.location : null;

  const actions = a
    ? [
        { label: "Storing loggen", icon: AlertTriangle, testid: "scan-log-fault", onClick: () => navigate(`/storingen?new=1&loc=${a.location_id || ""}&asset=${a.id}`) },
        { label: "Punt toevoegen", icon: ListPlus, testid: "scan-add-punt", onClick: () => setPuntOpen(true) },
        { label: "Reparatie loggen", icon: Wrench, testid: "scan-log-repair", onClick: () => navigate(`/assets/${a.id}?tab=reparaties`) },
        { label: "Open asset", icon: ChevronRight, testid: "scan-open-asset", onClick: () => navigate(`/assets/${a.id}`) },
      ]
    : [
        { label: "Storing loggen", icon: AlertTriangle, testid: "scan-log-fault", onClick: () => navigate(`/storingen?new=1&loc=${loc?.location_id || ""}`) },
        { label: "Punt toevoegen", icon: ListPlus, testid: "scan-add-punt", onClick: () => setPuntOpen(true) },
        { label: "Open locatie", icon: ChevronRight, testid: "scan-open-location", onClick: () => navigate(`/locaties/${loc?.location_id}`) },
      ];

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6" data-testid="scan-staff">
      <div className="mx-auto w-full max-w-md space-y-4">
        {a ? (
          <Plaatkop
            testid="scan-plaatkop"
            code={a.asset_code}
            subtitle={[a.name, a.location_name || "Zonder locatie", a.ruimte_name, a.spoor].filter(Boolean).join(" · ")}
            collapsibleFields
            fields={[
              { label: "Merk", value: a.brand },
              { label: "Type", value: a.model },
              { label: "Serienr", value: a.serial_number, mono: true },
              { label: "Bouwjaar", value: a.purchase_date ? a.purchase_date.slice(0, 4) : null, mono: true },
              { label: "Locatie", value: a.location_name },
              { label: "Ruimte", value: a.ruimte_name },
              { label: "Spoor", value: a.spoor },
            ]}
          />
        ) : (
          <Plaatkop testid="scan-plaatkop" code={loc?.name} subtitle="Locatiesticker" />
        )}

        <div className="space-y-2">
          {actions.map((act) => (
            <Button
              key={act.testid}
              data-testid={act.testid}
              variant="secondary"
              className="h-14 w-full justify-start text-body"
              onClick={act.onClick}
            >
              <act.icon size={20} className="mr-3" /> {act.label}
            </Button>
          ))}
        </div>
      </div>

      <PuntQuickDialog
        open={puntOpen}
        onOpenChange={setPuntOpen}
        presetLocationId={a?.location_id || loc?.location_id}
        presetLocationName={a?.location_name || loc?.name}
      />
    </div>
  );
};

export default function ScanLanding() {
  const { qrId } = useParams();
  const { user, loading, hasRole } = useAuth();

  if (loading)
    return (
      <div className="min-h-screen bg-background p-4">
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (user && hasRole("owner", "td")) return <StaffScan qrId={qrId} />;
  return <PublicReport />;
}
